import React, { useState, useEffect, useRef } from 'react';
import { MAX_CONTENT_WIDTH } from '../constants/layout';

// ---------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------

const TIERS = [
  {
    id: 'free',
    name: 'Free',
    tagline: 'Get started',
    bestFor: 'Most short films, class assignments, passion projects',
    gradient: 'linear-gradient(135deg, rgb(102,102,115), rgb(140,140,153))',
    colors: {
      primary: 'rgb(102,102,115)',
      secondary: 'rgb(140,140,153)',
      accent: 'rgb(115,120,133)',
    },
    individual: {
      monthly: { price: 0 },
      annual: { price: 0 },
    },
    team: null,
    scenes: 'Up to 15',
    collaborators: 'Up to 10',
    popular: false,
  },
  {
    id: 'education',
    name: 'Education',
    tagline: 'For learners',
    bestFor: 'Thesis films, film school projects, classroom demos',
    gradient: 'linear-gradient(135deg, rgb(26,64,191), rgb(89,26,179))',
    colors: {
      primary: 'rgb(26,64,191)',
      secondary: 'rgb(89,26,179)',
      accent: 'rgb(38,115,230)',
    },
    individual: {
      monthly: { price: 2.99 },
      annual: { price: 2.49, annualTotal: 29.88, savings: 17 },
    },
    team: null,
    scenes: 'Up to 40',
    collaborators: 'Up to 30',
    billingNote: 'Requires .edu email (students & educators)',
    popular: false,
  },
  {
    id: 'indie',
    name: 'Indie',
    tagline: 'For creators',
    bestFor: 'Short films, music videos, spec ads, web series episodes',
    gradient: 'linear-gradient(135deg, rgb(0,140,115), rgb(13,89,140))',
    colors: {
      primary: 'rgb(0,140,115)',
      secondary: 'rgb(13,89,140)',
      accent: 'rgb(0,166,77)',
    },
    individual: {
      monthly: { price: 8.99 },
      annual: { price: 6.99, annualTotal: 83.88, savings: 22 },
    },
    team: {
      monthly: { price: 4.99 },
      annual: { price: 3.99, annualTotal: 47.88, savings: 20 },
      minSeats: 3,
    },
    scenes: 'Up to 40',
    collaborators: 'Up to 30',
    popular: false,
  },
  {
    id: 'pro',
    name: 'Pro',
    tagline: 'For professionals',
    bestFor: 'TV pilots, web series, low-budget features, festival films',
    gradient: 'linear-gradient(135deg, rgb(217,38,26), rgb(191,13,89))',
    colors: {
      primary: 'rgb(217,38,26)',
      secondary: 'rgb(191,13,89)',
      accent: 'rgb(230,102,0)',
    },
    individual: {
      monthly: { price: 19.99 },
      annual: { price: 15.99, annualTotal: 191.88, savings: 20 },
    },
    team: {
      monthly: { price: 10.99 },
      annual: { price: 8.99, annualTotal: 107.88, savings: 18 },
      minSeats: 3,
    },
    scenes: 'Up to 80',
    collaborators: 'Up to 50',
    popular: true,
  },
  {
    id: 'max',
    name: 'Max',
    tagline: 'For production teams',
    bestFor: 'Features, TV productions, large-scale commercial work',
    gradient: 'linear-gradient(135deg, rgb(64,13,153), rgb(128,26,179))',
    colors: {
      primary: 'rgb(64,13,153)',
      secondary: 'rgb(128,26,179)',
      accent: 'rgb(38,0,128)',
    },
    individual: {
      monthly: { price: 49.99 },
      annual: { price: 39.99, annualTotal: 479.88, savings: 20 },
    },
    team: {
      monthly: { price: 24.99 },
      annual: { price: 19.99, annualTotal: 239.88, savings: 20 },
      minSeats: 5,
    },
    scenes: 'Unlimited',
    collaborators: 'Unlimited',
    popular: false,
  },
];

const FeatureName = Object.freeze({
  scenes: 'Scenes (per Project)',
  collaborators: 'Collaborators (per Project)',
  projects: 'Projects',
  stripBoardScheduling: 'Strip Board Scheduling',
  shotListing: 'Shot Listing',
  castCrewList: 'Cast & Crew List',
  scriptBreakdown: 'Script Breakdown',
  callSheets: 'Call Sheets',
  calendars: 'Calendars',
  callSheetExport: 'Call Sheet Export (PDF)',
  pdfExport: 'PDF Export (All Docs)',
  offlineMode: 'Offline Mode',
  platforms: 'iPhone, iPad & Mac',
  doodReports: 'DOOD Reports',
  scriptImport: 'Script Import',
  imageAttachments: 'Image Attachments',
  support: 'Support (24/7)',
});

const ALL_FEATURES = [
  { name: FeatureName.scenes, perTier: true },
  { name: FeatureName.collaborators, perTier: true },
  { name: FeatureName.projects, universal: 'Unlimited' },
  { name: FeatureName.stripBoardScheduling },
  { name: FeatureName.shotListing },
  { name: FeatureName.castCrewList },
  { name: FeatureName.scriptBreakdown },
  { name: FeatureName.callSheets },
  { name: FeatureName.calendars },
  { name: FeatureName.callSheetExport },
  { name: FeatureName.pdfExport },
  { name: FeatureName.offlineMode },
  { name: FeatureName.platforms },
  { name: FeatureName.doodReports },
  { name: FeatureName.scriptImport },
  { name: FeatureName.imageAttachments },
  { name: FeatureName.support },
];

// ---------------------------------------------------------------------------
// CSS Keyframes (injected once)
// ---------------------------------------------------------------------------

const ANIMATION_STYLES = `
  @keyframes tierCardEnter {
    from {
      opacity: 0;
      transform: scale(0.85);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
  @keyframes tierCardExit {
    from {
      opacity: 1;
      transform: scale(1);
    }
    to {
      opacity: 0;
      transform: scale(0.85);
    }
  }
  @keyframes priceSlideIn {
    from {
      opacity: 0;
      transform: translateY(6px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  @keyframes fadeSlideDown {
    from {
      opacity: 0;
      transform: translateY(-8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes comparisonSlideIn {
    from {
      opacity: 0;
      transform: translateY(-12px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatPrice(price) {
  if (price === 0) return '$0';
  return `$${price.toFixed(2)}`;
}

function getPricing(tier, billingMode, billingPeriod) {
  const modeData = billingMode === 'team' ? tier.team : tier.individual;
  if (!modeData) return null;
  return modeData[billingPeriod];
}

function getPeriodLabel(tier, billingMode, billingPeriod) {
  if (tier.id === 'free') return '';
  if (billingMode === 'team') return '/seat/mo';
  return billingPeriod === 'monthly' ? 'for 1 month' : '/mo';
}

function getBillingDescription(tier, billingMode, billingPeriod) {
  if (tier.id === 'free') return 'Every feature included. Free.';
  if (billingMode === 'individual' && billingPeriod === 'monthly') return 'Month pass, no auto-renew';
  if (billingMode === 'individual' && billingPeriod === 'annual') return 'Auto-renews yearly. Cancel anytime.';
  if (billingMode === 'team' && billingPeriod === 'monthly') return 'Auto-renews monthly. Cancel anytime.';
  return 'Auto-renews yearly. Cancel anytime.';
}

function getFeatureValue(feature, tier) {
  if (feature.perTier) {
    if (feature.name === FeatureName.scenes) return tier.scenes;
    if (feature.name === FeatureName.collaborators) return tier.collaborators;
  }
  if (feature.universal) return feature.universal;
  return true;
}

// Snappy spring-like easing (approximates SwiftUI .snappy)
const SNAPPY_EASING = 'cubic-bezier(0.2, 0.8, 0.2, 1)';

// ---------------------------------------------------------------------------
// Background Shapes (matching TierCardBackground.swift)
// ---------------------------------------------------------------------------

function TierCardBackground({ tier }) {
  const { colors } = tier;
  const isFree = tier.id === 'free';

  const shapeStyle = (color, opacity, width, height, x, y, blur, rotate) => ({
    position: 'absolute',
    width: `${width}px`,
    height: `${height}px`,
    borderRadius: '50%',
    background: color,
    opacity,
    filter: `blur(${blur}px)`,
    left: `calc(50% + ${x}px)`,
    top: `calc(50% + ${y}px)`,
    transform: `translate(-50%, -50%)${rotate ? ` rotate(${rotate}deg)` : ''}`,
    pointerEvents: 'none',
  });

  const shapes = {
    free: (
      <>
        <div style={shapeStyle(colors.accent, 0.3, 140, 140, -60, -80, 20)} />
        <div style={shapeStyle(colors.primary, 0.4, 100, 160, 70, 120, 25)} />
      </>
    ),
    education: (
      <>
        <div style={shapeStyle(colors.accent, 0.5, 180, 180, 80, -60, 30)} />
        <div style={shapeStyle(colors.secondary, 0.4, 120, 120, -50, 100, 20)} />
        <div style={shapeStyle(colors.primary, 0.3, 90, 140, 30, 160, 15)} />
      </>
    ),
    indie: (
      <>
        <div style={shapeStyle(colors.accent, 0.5, 200, 120, -40, -70, 20, -25)} />
        <div style={shapeStyle(colors.secondary, 0.45, 150, 150, 60, 80, 25)} />
        <div style={shapeStyle(colors.primary, 0.3, 80, 130, -70, 140, 15, 40)} />
      </>
    ),
    pro: (
      <>
        <div style={shapeStyle(colors.accent, 0.55, 160, 160, -50, -50, 20)} />
        <div style={shapeStyle(colors.secondary, 0.5, 180, 100, 60, 60, 18, 30)} />
        <div style={shapeStyle(colors.accent, 0.4, 100, 100, -30, 180, 15)} />
        <div style={shapeStyle(colors.primary, 0.3, 120, 60, 70, -80, 12, -15)} />
      </>
    ),
    max: (
      <>
        <div style={shapeStyle(colors.accent, 0.5, 200, 140, 50, -60, 25, -35)} />
        <div style={shapeStyle(colors.secondary, 0.45, 170, 170, -60, 50, 20)} />
        <div style={shapeStyle(colors.primary, 0.35, 100, 160, 40, 150, 18, 50)} />
        <div style={shapeStyle(colors.accent, 0.25, 80, 80, -40, -90, 12)} />
      </>
    ),
  };

  if (isFree) return null;

  return (
    <div style={{
      position: 'absolute',
      inset: 0,
      overflow: 'hidden',
      borderRadius: '16px',
      pointerEvents: 'none',
    }}>
      {shapes[tier.id]}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function TogglePicker({ options, value, onChange }) {
  return (
    <div style={{
      display: 'inline-flex',
      backgroundColor: 'var(--color-surface-muted)',
      border: '1px solid var(--color-border)',
      borderRadius: '10px',
      padding: '4px',
      gap: '4px',
    }}>
      {options.map(opt => (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          style={{
            padding: '10px 24px',
            border: 'none',
            borderRadius: '8px',
            backgroundColor: value === opt.value ? 'var(--color-surface)' : 'transparent',
            color: value === opt.value ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
            fontSize: '15px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease',
            fontFamily: 'Helvetica, Arial, sans-serif',
            boxShadow: value === opt.value ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
          }}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function TierCard({ tier, billingMode, billingPeriod, isMobile, animationDelay }) {
  const pricing = getPricing(tier, billingMode, billingPeriod);
  if (!pricing) return null;

  const periodLabel = getPeriodLabel(tier, billingMode, billingPeriod);
  const billingDesc = getBillingDescription(tier, billingMode, billingPeriod);
  const isFree = tier.id === 'free';
  const annualTotal = billingPeriod === 'annual' ? pricing.annualTotal : null;
  const savings = billingPeriod === 'annual' ? pricing.savings : null;

  const textColor = isFree ? 'var(--color-text-primary)' : '#ffffff';
  const subtleColor = isFree ? 'var(--color-text-secondary)' : 'rgba(255,255,255,0.7)';
  const faintColor = isFree ? 'var(--color-text-secondary)' : 'rgba(255,255,255,0.5)';
  const dividerColor = isFree ? 'var(--color-border)' : 'rgba(255,255,255,0.2)';

  // Unique key for price animation on billing changes
  const priceKey = `${pricing.price}-${billingPeriod}-${billingMode}`;

  return (
    <div
      style={{
        background: isFree ? 'var(--color-surface)' : tier.gradient,
        borderRadius: '16px',
        padding: '28px 24px',
        border: isFree ? '1px solid var(--color-border)' : 'none',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        width: isMobile ? '280px' : '240px',
        minHeight: '420px',
        flexShrink: 0,
        scrollSnapAlign: 'start',
        overflow: 'hidden',
        boxShadow: tier.popular
          ? '0 8px 32px rgba(0,0,0,0.2)'
          : '0 4px 16px rgba(0,0,0,0.1)',
        animation: `tierCardEnter 0.4s ${SNAPPY_EASING} both`,
        animationDelay: `${animationDelay}s`,
        transition: 'transform 0.25s ease, box-shadow 0.25s ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-4px)';
        e.currentTarget.style.boxShadow = tier.popular
          ? '0 12px 40px rgba(0,0,0,0.25)'
          : '0 8px 24px rgba(0,0,0,0.15)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = tier.popular
          ? '0 8px 32px rgba(0,0,0,0.2)'
          : '0 4px 16px rgba(0,0,0,0.1)';
      }}
    >
      {/* Blurred gradient shapes */}
      <TierCardBackground tier={tier} />

      {tier.popular && (
        <div style={{
          position: 'absolute',
          top: '14px',
          right: '14px',
          backgroundColor: 'rgba(255,255,255,0.95)',
          color: '#111',
          padding: '4px 12px',
          borderRadius: '20px',
          fontSize: '11px',
          fontWeight: '700',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          fontFamily: 'Helvetica, Arial, sans-serif',
          zIndex: 1,
        }}>
          Popular
        </div>
      )}

      {/* Header */}
      <div style={{ marginBottom: '4px', position: 'relative', zIndex: 1 }}>
        <div style={{
          fontSize: '22px',
          fontWeight: '700',
          color: textColor,
          letterSpacing: '-0.03em',
          fontFamily: 'Helvetica, Arial, sans-serif',
        }}>
          {tier.name}
        </div>
        <div style={{
          fontSize: '13px',
          color: faintColor,
          marginTop: '4px',
          lineHeight: '1.4',
          fontFamily: 'Helvetica, Arial, sans-serif',
        }}>
          {tier.bestFor}
        </div>
      </div>

      {/* Price — animated on change */}
      <div key={priceKey} style={{
        margin: '20px 0 16px',
        position: 'relative',
        zIndex: 1,
        animation: `priceSlideIn 0.35s ${SNAPPY_EASING} both`,
      }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
          <span style={{
            fontSize: '42px',
            fontWeight: '700',
            color: textColor,
            lineHeight: '1',
            letterSpacing: '-0.04em',
            fontFamily: 'Helvetica, Arial, sans-serif',
          }}>
            {formatPrice(pricing.price)}
          </span>
          {periodLabel && (
            <span style={{
              fontSize: '14px',
              color: subtleColor,
              fontFamily: 'Helvetica, Arial, sans-serif',
            }}>
              {periodLabel}
            </span>
          )}
        </div>
        {annualTotal && annualTotal > 0 ? (
          <div style={{
            fontSize: '13px',
            color: subtleColor,
            marginTop: '4px',
            fontFamily: 'Helvetica, Arial, sans-serif',
          }}>
            Billed {billingMode === 'team'
              ? `$${annualTotal.toFixed(2)}/seat/yr`
              : `$${annualTotal.toFixed(2)}/yr`}
          </div>
        ) : (
          <div style={{ height: '18px' }} />
        )}
        {savings ? (
          <div style={{
            display: 'inline-block',
            fontSize: '12px',
            fontWeight: '600',
            color: isFree ? '#16a34a' : '#4ade80',
            marginTop: '4px',
            fontFamily: 'Helvetica, Arial, sans-serif',
          }}>
            Save {savings}%
          </div>
        ) : (
          <div style={{ height: '17px' }} />
        )}
        <div style={{
          fontSize: '12px',
          color: faintColor,
          marginTop: '6px',
          fontFamily: 'Helvetica, Arial, sans-serif',
          transition: `opacity 0.3s ease`,
        }}>
          {billingDesc}
        </div>
      </div>

      {/* Seat minimum note */}
      {billingMode === 'team' && tier.team?.minSeats && (
        <div style={{
          fontSize: '12px',
          color: subtleColor,
          marginBottom: '8px',
          fontFamily: 'Helvetica, Arial, sans-serif',
          position: 'relative',
          zIndex: 1,
          animation: `fadeIn 0.3s ease both`,
        }}>
          Min {tier.team.minSeats} seats
        </div>
      )}

      {/* Tier-specific billing note */}
      {tier.billingNote && (
        <div style={{
          fontSize: '12px',
          color: isFree ? '#f59e0b' : 'rgba(255,255,255,0.8)',
          marginBottom: '8px',
          fontFamily: 'Helvetica, Arial, sans-serif',
          position: 'relative',
          zIndex: 1,
        }}>
          {tier.billingNote}
        </div>
      )}

      {/* Divider */}
      <div style={{
        height: '1px',
        backgroundColor: dividerColor,
        margin: '4px 0 16px',
        position: 'relative',
        zIndex: 1,
      }} />

      {/* Key features */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        flex: 1,
        position: 'relative',
        zIndex: 1,
      }}>
        <KeyFeature label="Projects" value="Unlimited" textColor={textColor} subtleColor={subtleColor} />
        <KeyFeature label="Scenes" value={tier.scenes} textColor={textColor} subtleColor={subtleColor} />
        <KeyFeature label="Collaborators" value={tier.collaborators} textColor={textColor} subtleColor={subtleColor} />
      </div>

      {/* CTA */}
      <button
        style={{
          marginTop: '20px',
          padding: '12px 20px',
          border: 'none',
          borderRadius: '8px',
          backgroundColor: isFree ? 'var(--color-accent)' : 'rgba(255,255,255,0.95)',
          color: isFree ? '#ffffff' : '#111',
          fontSize: '15px',
          fontWeight: '600',
          cursor: 'pointer',
          transition: 'transform 0.15s ease, opacity 0.15s ease',
          fontFamily: 'Helvetica, Arial, sans-serif',
          textAlign: 'center',
          width: '100%',
          position: 'relative',
          zIndex: 1,
        }}
        onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.9'; e.currentTarget.style.transform = 'scale(1.02)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'scale(1)'; }}
      >
        {isFree ? 'Get Started Free' : `Choose ${tier.name}`}
      </button>
    </div>
  );
}

function KeyFeature({ label, value, textColor, subtleColor }) {
  const isUnlimited = value === 'Unlimited';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <span style={{
        fontSize: '14px',
        color: isUnlimited ? (textColor === '#ffffff' ? '#ffffff' : '#3b82f6') : subtleColor,
        flexShrink: 0,
        width: '18px',
        textAlign: 'center',
      }}>
        {isUnlimited ? '∞' : '•'}
      </span>
      <div>
        <div style={{
          fontSize: '12px',
          color: subtleColor,
          fontFamily: 'Helvetica, Arial, sans-serif',
        }}>
          {label}
        </div>
        <div style={{
          fontSize: '14px',
          color: textColor,
          fontWeight: '500',
          fontFamily: 'Helvetica, Arial, sans-serif',
        }}>
          {value}
        </div>
      </div>
    </div>
  );
}

function FeatureComparisonTable({ visibleTiers, isMobile }) {
  return (
    <div style={{
      overflowX: 'auto',
      WebkitOverflowScrolling: 'touch',
      borderRadius: '12px',
      border: '1px solid var(--color-border)',
      backgroundColor: 'var(--color-surface)',
      animation: `comparisonSlideIn 0.35s ${SNAPPY_EASING} both`,
    }}>
      <table style={{
        width: '100%',
        minWidth: isMobile ? '600px' : 'auto',
        borderCollapse: 'collapse',
        fontFamily: 'Helvetica, Arial, sans-serif',
      }}>
        <thead>
          <tr>
            <th style={{
              textAlign: 'left',
              padding: '14px 16px',
              fontSize: '13px',
              fontWeight: '600',
              color: 'var(--color-text-secondary)',
              borderBottom: '1px solid var(--color-border)',
              position: isMobile ? 'sticky' : 'static',
              left: 0,
              backgroundColor: 'var(--color-surface)',
              minWidth: '180px',
            }}>
              Feature
            </th>
            {visibleTiers.map(tier => (
              <th key={tier.id} style={{
                textAlign: 'center',
                padding: '14px 12px',
                fontSize: '13px',
                fontWeight: '600',
                color: 'var(--color-text-primary)',
                borderBottom: '1px solid var(--color-border)',
                minWidth: '100px',
              }}>
                {tier.name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {ALL_FEATURES.map((feature, idx) => (
            <tr key={feature.name} style={{
              backgroundColor: idx % 2 === 1 ? 'var(--color-surface-muted)' : 'transparent',
            }}>
              <td style={{
                padding: '10px 16px',
                fontSize: '14px',
                color: 'var(--color-text-primary)',
                borderBottom: '1px solid var(--color-border)',
                position: isMobile ? 'sticky' : 'static',
                left: 0,
                backgroundColor: idx % 2 === 1 ? 'var(--color-surface-muted)' : 'var(--color-surface)',
              }}>
                {feature.name}
              </td>
              {visibleTiers.map(tier => {
                const val = getFeatureValue(feature, tier);
                return (
                  <td key={tier.id} style={{
                    textAlign: 'center',
                    padding: '10px 12px',
                    fontSize: '14px',
                    borderBottom: '1px solid var(--color-border)',
                    color: val === false ? 'var(--color-text-secondary)' : 'var(--color-text-primary)',
                    opacity: val === false ? 0.4 : 1,
                  }}>
                    {val === true ? (
                      <span style={{ color: '#16a34a', fontWeight: '600' }}>&#10003;</span>
                    ) : val === false ? (
                      '—'
                    ) : (
                      val
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page Component
// ---------------------------------------------------------------------------

const FilmmakingPricingPage = () => {
  const [billingMode, setBillingMode] = useState('individual');
  const [billingPeriod, setBillingPeriod] = useState('annual');
  const [isMobile, setIsMobile] = useState(false);

  // Track billingMode changes to re-trigger card enter animations
  const modeKeyRef = useRef(0);
  const prevModeRef = useRef(billingMode);
  if (prevModeRef.current !== billingMode) {
    prevModeRef.current = billingMode;
    modeKeyRef.current += 1;
  }

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const visibleTiers = billingMode === 'team'
    ? TIERS.filter(t => t.team !== null)
    : TIERS;

  const modeOptions = [
    { value: 'individual', label: 'Individual' },
    { value: 'team', label: 'Team' },
  ];

  const periodOptions = billingMode === 'individual'
    ? [
        { value: 'annual', label: 'Annual Plan' },
        { value: 'monthly', label: '1 Month Pass' },
      ]
    : [
        { value: 'annual', label: 'Annual Plan' },
        { value: 'monthly', label: 'Monthly' },
      ];

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: 'var(--color-background)',
      paddingBottom: '80px',
    }}>
      {/* Inject animation keyframes */}
      <style>{ANIMATION_STYLES}</style>

      {/* Header */}
      <div style={{
        maxWidth: MAX_CONTENT_WIDTH,
        margin: '0 auto',
        padding: '40px 20px 0',
        textAlign: 'center',
        marginBottom: '40px',
      }}>
        <h1 style={{
          color: 'var(--color-text-primary)',
          fontSize: isMobile ? '32px' : '44px',
          fontWeight: '700',
          marginBottom: '12px',
          letterSpacing: '-0.05em',
          fontFamily: 'Helvetica, Arial, sans-serif',
          lineHeight: '1.1',
        }}>
          6 apps. Unlimited projects.{isMobile ? ' ' : <br />}No subscription required.
        </h1>
        <p style={{
          color: 'var(--color-text-secondary)',
          fontSize: '17px',
          lineHeight: '1.5',
          maxWidth: '500px',
          margin: '0 auto',
          letterSpacing: '-0.02em',
          fontFamily: 'Helvetica, Arial, sans-serif',
        }}>
          One bundle that scales with your production's scope.
        </p>
      </div>

      {/* Toggles */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        gap: '12px',
        marginBottom: '16px',
        flexWrap: 'wrap',
        padding: '0 20px',
      }}>
        <TogglePicker options={modeOptions} value={billingMode} onChange={setBillingMode} />
        <TogglePicker options={periodOptions} value={billingPeriod} onChange={setBillingPeriod} />
      </div>

      {/* Period hint — fade transition */}
      <div style={{
        textAlign: 'center',
        marginBottom: '32px',
        minHeight: '20px',
      }}>
        {billingPeriod === 'monthly' && billingMode === 'individual' && (
          <span
            key="no-autorenew-hint"
            style={{
              fontSize: '13px',
              fontWeight: '600',
              color: '#f59e0b',
              fontFamily: 'Helvetica, Arial, sans-serif',
              animation: `fadeIn 0.25s ease both`,
            }}
          >
            No auto-renew. Buy again when you need it.
          </span>
        )}
      </div>

      {/* Team perk banner — fade+slide transition */}
      {billingMode === 'team' && (
        <div
          key="team-perk-banner"
          style={{
            maxWidth: MAX_CONTENT_WIDTH,
            margin: '0 auto 32px',
            padding: '0 20px',
            animation: `fadeSlideDown 0.35s ${SNAPPY_EASING} both`,
          }}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            justifyContent: 'center',
            padding: '14px 20px',
            borderRadius: '10px',
            backgroundColor: 'rgba(245,158,11,0.08)',
            border: '1px solid rgba(245,158,11,0.15)',
          }}>
            <span style={{ fontSize: '18px' }}>&#127873;</span>
            <span style={{
              fontSize: '14px',
              color: 'var(--color-text-primary)',
              fontFamily: 'Helvetica, Arial, sans-serif',
            }}>
              Every paid seat includes a free Indie personal workspace.
            </span>
          </div>
        </div>
      )}

      {/* Tier Cards — keyed on billingMode so cards re-enter with animation */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        marginBottom: '48px',
      }}>
        <div
          key={`cards-${modeKeyRef.current}`}
          style={{
            display: 'flex',
            gap: '16px',
            overflowX: 'auto',
            paddingLeft: '20px',
            paddingRight: '20px',
            paddingTop: '8px',
            paddingBottom: '24px',
            scrollSnapType: 'x mandatory',
            WebkitOverflowScrolling: 'touch',
            maxWidth: '100%',
            width: 'fit-content',
          }}
        >
          {visibleTiers.map((tier, idx) => (
            <TierCard
              key={tier.id}
              tier={tier}
              billingMode={billingMode}
              billingPeriod={billingPeriod}
              isMobile={isMobile}
              animationDelay={idx * 0.06}
            />
          ))}
        </div>
      </div>

      {/* Footnotes */}
      <div style={{
        maxWidth: MAX_CONTENT_WIDTH,
        margin: '0 auto 48px',
        padding: '0 20px',
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '6px',
        }}>
          {[
            'Individual Monthly is a month pass with no auto-renew — buy again when needed.',
            'Individual Annual and all Seat plans auto-renew. Cancel anytime.',
            ...(billingMode === 'team' ? ['Seat minimums apply: 3 seats for Indie/Pro, 5 seats for Max.'] : []),
            'Education plan requires a .edu email (students & educators), verified annually.',
          ].map((note, i) => (
            <p key={note} style={{
              fontSize: '13px',
              color: 'var(--color-text-secondary)',
              margin: 0,
              opacity: 0.7,
              fontFamily: 'Helvetica, Arial, sans-serif',
              lineHeight: '1.5',
              animation: billingMode === 'team' && note.includes('Seat minimums')
                ? `fadeIn 0.3s ease both`
                : undefined,
            }}>
              {note}
            </p>
          ))}
        </div>
      </div>

      {/* Feature Comparison */}
      <div style={{
        maxWidth: MAX_CONTENT_WIDTH,
        margin: '0 auto',
        padding: '0 20px',
      }}>
        <h2 style={{
          fontSize: '24px',
          fontWeight: '700',
          color: 'var(--color-text-primary)',
          fontFamily: 'Helvetica, Arial, sans-serif',
          letterSpacing: '-0.03em',
          marginBottom: '20px',
        }}>
          Compare All Features
        </h2>
        <FeatureComparisonTable visibleTiers={visibleTiers} isMobile={isMobile} />
      </div>

      {/* USD note */}
      <div style={{
        textAlign: 'center',
        marginTop: '60px',
        padding: '0 20px',
      }}>
        <p style={{
          fontSize: '12px',
          color: 'var(--color-text-secondary)',
          fontWeight: '600',
          letterSpacing: '0.05em',
          margin: 0,
          fontFamily: 'Helvetica, Arial, sans-serif',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
        }}>
          ALL PRICES IN US DOLLARS
        </p>
      </div>
    </div>
  );
};

export default FilmmakingPricingPage;
